import {IAutoOrderOrder} from '../models';
import {resolveAutoOrderPreparedItemKey} from './auto-order-prepared-item-key.utils';

/**
 * Counts the prepared lines that would still be on the order after the given
 * stock codes are removed.
 *
 * Codes are matched through {@link resolveAutoOrderPreparedItemKey} so the count
 * follows the same key resolution the removal itself uses. Failing codes that do
 * not match a prepared line are ignored rather than counted, because they cannot
 * remove anything from the order.
 */
export function countRemainingPreparedItems(order: IAutoOrderOrder | undefined, codesToRemove: string[]): number {
  const preparedItems: Record<string, unknown> | undefined = order?.preparedInfo?.preparedItems;
  if (!preparedItems) {
    return 0;
  }

  const preparedKeys: string[] = Object.keys(preparedItems);
  const keysToRemove = new Set<string>();
  codesToRemove.forEach((code: string): void => {
    const key: string | undefined = resolveAutoOrderPreparedItemKey(preparedItems, code);
    if (key) {
      keysToRemove.add(key);
    }
  });

  return preparedKeys.filter((key: string): boolean => !keysToRemove.has(key)).length;
}

/**
 * Returns true when removing the given stock codes would leave the order with no
 * prepared lines to submit, which is when partial retry must be blocked.
 */
export function wouldPartialRetryEmptyOrder(order: IAutoOrderOrder | undefined, codesToRemove: string[]): boolean {
  return countRemainingPreparedItems(order, codesToRemove) === 0;
}
